import React, { useState, useEffect } from 'react';
import { TextField, ActionButton, Icon, MessageBar, MessageBarType } from '@fluentui/react';
import { getEntityType, MarketplaceOffer } from '@the-real-insight/tri-model';
import { useModalContext } from '../components/ModalProvider';
import { useTranslation } from 'react-i18next';

interface SampleRequestsProps {
    type: any;
    marketplaceOffer: MarketplaceOffer;
    setMarketplaceOffer: (marketplaceOffer: MarketplaceOffer) => void;
}

export default function SampleRequests({
    type,
    marketplaceOffer,
    setMarketplaceOffer,
}: SampleRequestsProps) {
    const { t } = useTranslation();
    const { pushError } = useModalContext() as any;
    const [newRequestText, setNewRequestText] = useState('');
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    // Debug: Monitor changes to sampleRequests
    useEffect(() => {
        console.log('sampleRequests changed:', marketplaceOffer.sampleRequests);
        console.log('sampleRequests length:', marketplaceOffer.sampleRequests?.length || 0);
    }, [marketplaceOffer.sampleRequests]);

    const validateRequest = (requestText: string): string[] => {
        const errors: string[] = [];
        if (!requestText.trim()) {
            errors.push('Beispielanfrage ist erforderlich');
        }
        return errors;
    };

    const addSampleRequest = () => {
        const errors = validateRequest(newRequestText);
        if (errors.length > 0) {
            setValidationErrors(errors);
            return;
        }

        const newRequest = newRequestText.trim();
        
        // Create a new marketplaceOffer object with the updated sampleRequests
        const updatedMarketplaceOffer = {
            ...marketplaceOffer,
            sampleRequests: [...(marketplaceOffer.sampleRequests || []), newRequest]
        } as MarketplaceOffer;
        setMarketplaceOffer(updatedMarketplaceOffer);
        setNewRequestText('');
        setValidationErrors([]);
    };

    const removeSampleRequest = (index: number) => {
        console.log('removeSampleRequest called', index);
        // Create a new marketplaceOffer object with the updated sampleRequests
        const updatedMarketplaceOffer = {
            ...marketplaceOffer,
            sampleRequests: (marketplaceOffer.sampleRequests || []).filter((_: string, i: number) => i !== index)
        } as MarketplaceOffer;
        setMarketplaceOffer(updatedMarketplaceOffer);
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            addSampleRequest();
        }
    };

    return (
        <div className="width600">
            <div className="marginBottomM">
                <div className="textHeading">Beispielanfragen</div>
                <div className="marginTopS textM">
                    Beispielanfragen helfen Nutzern zu verstehen, wie sie mit Ihrem Agent Toolset interagieren können.
                </div>
            </div>

            {validationErrors.length > 0 && (
                <MessageBar messageBarType={MessageBarType.error} className="marginBottomM">
                    <div>
                        {validationErrors.map((error, index) => (
                            <div key={index}>{error}</div>
                        ))}
                    </div>
                </MessageBar>
            )}

            <div className="displayFlex gapS marginBottomL">
                <div className="flexGrow1">
                    <TextField
                        placeholder="Neue Beispielanfrage eingeben..."
                        value={newRequestText}
                        onChange={(e, value) => {
                            setNewRequestText(value || '');
                            if (validationErrors.length > 0) {
                                setValidationErrors([]);
                            }
                        }}
                        onKeyPress={handleKeyPress}
                    />
                </div>
                <ActionButton
                    iconProps={{ iconName: 'Add' }}
                    onClick={addSampleRequest}
                    disabled={!newRequestText.trim()}
                    title="Beispielanfrage hinzufügen"
                />
            </div>

            {(marketplaceOffer.sampleRequests || []).length === 0 ? (
                <div className="paddingM backgroundColorLightestGrey textCenter">
                    Keine Beispielanfragen vorhanden.
                </div>
            ) : (
                <div>
                    {(marketplaceOffer.sampleRequests || []).map((request: string, index: number) => (
                        <div key={index} className="displayFlex alignItemsCenter justifyContentBetween paddingS marginBottomS borderNeutral backgroundColorLightestGrey">
                            <div className="flexGrow1">
                                <div className="fontWeightBold">{request}</div>
                            </div>
                            <ActionButton
                                iconProps={{ iconName: 'Delete' }}
                                onClick={() => removeSampleRequest(index)}
                                title="Beispielanfrage löschen"
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
} 